import { Loader2 } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { ReasonDialog } from "../operations/OperationDocumentDialogs";
import { formatMxn } from "../quoter/PlanSchedule";
import type { Project } from "../types";
import { ReceivableApproveDialog } from "./ReceivableApproveDialog";
import type { PayableSummary, ReceivableSummary } from "./invoicingTypes";
import { isDeveloperPaid } from "./invoicingScreenRules";
import { useInvoicingRun } from "./useInvoicingRun";

const EMPTY = "—";

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm tabular-nums">{children}</dd>
  </div>
);

const Amounts = ({
  row,
}: {
  row: Pick<ReceivableSummary, "subtotal" | "iva_amount" | "total_amount">;
}) => {
  const translate = useTranslate();
  return (
    <>
      <Fact label={translate("crm.invoicing.fields.subtotal")}>
        {formatMxn(row.subtotal)}
      </Fact>
      <Fact label={translate("crm.invoicing.fields.iva_amount")}>
        {formatMxn(row.iva_amount)}
      </Fact>
      <Fact label={translate("crm.invoicing.fields.total")}>
        <span className="font-semibold">{formatMxn(row.total_amount)}</span>
      </Fact>
    </>
  );
};

const LastRejection = ({ reason }: { reason?: string | null }) => {
  const translate = useTranslate();
  if (!reason) return null;
  return (
    <p className="text-sm text-[#B3321C]" data-testid="last-rejection">
      {translate("crm.invoicing.vobo.last_rejection", { reason })}
    </p>
  );
};

/** Loads the project's current template, then opens the approval dialog. */
const ApproveReceivableFlow = ({
  receivable,
  onClose,
}: {
  receivable: ReceivableSummary;
  onClose: () => void;
}) => {
  const run = useInvoicingRun();
  const developerPaid = isDeveloperPaid(receivable);
  const { data: projects, isPending } = useGetList<Project>(
    "projects",
    {
      filter: { code: receivable.project_code },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: developerPaid && receivable.project_code != null },
  );
  if (developerPaid && receivable.project_code != null && isPending) {
    return null;
  }
  return (
    <ReceivableApproveDialog
      receivable={receivable}
      template={
        developerPaid ? (projects?.[0]?.invoice_concept_template ?? null) : null
      }
      onClose={onClose}
      onSubmit={async (options) => {
        const message = await run(
          (provider) => provider.approveReceivable(receivable.id, options),
          "crm.invoicing.vobo.done.approved",
        );
        if (!message) onClose();
        return message;
      }}
    />
  );
};

/** A receivable awaiting Dirección's VoBo: approve (dialog) or reject (reason). */
export const PendingReceivableRow = ({
  receivable,
}: {
  receivable: ReceivableSummary;
}) => {
  const translate = useTranslate();
  const run = useInvoicingRun();
  const [dialog, setDialog] = useState<"approve" | "reject" | null>(null);
  const close = () => setDialog(null);

  return (
    <Card data-testid="pending-receivable">
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold tabular-nums">
            {receivable.operation_id} ·{" "}
            {translate("crm.invoicing.fields.installment_n", {
              n: receivable.installment_number,
            })}
          </h3>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="gradient"
              className="h-11 sm:h-9"
              onClick={() => setDialog("approve")}
            >
              {translate("crm.invoicing.vobo.approve")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={() => setDialog("reject")}
            >
              {translate("crm.invoicing.vobo.reject")}
            </Button>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Fact label={translate("crm.invoicing.fields.project")}>
            {receivable.project_name ?? EMPTY}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.unit")}>
            {receivable.unit_identifier ?? EMPTY}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.client")}>
            {receivable.client_name ?? receivable.payer_name ?? EMPTY}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.payer")}>
            {receivable.payer_name ??
              translate(`crm.invoicing.payer_type.${receivable.payer_type}`)}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.commission_pct")}>
            {receivable.commission_pct == null
              ? EMPTY
              : `${receivable.commission_pct}%`}
          </Fact>
          <Amounts row={receivable} />
        </dl>
        <LastRejection reason={receivable.last_rejection_reason} />
      </CardContent>
      {dialog === "approve" ? (
        <ApproveReceivableFlow receivable={receivable} onClose={close} />
      ) : null}
      {dialog === "reject" ? (
        <ReasonDialog
          title={translate("crm.invoicing.vobo.reject_receivable_title")}
          onClose={close}
          onSubmit={async (reason) => {
            const message = await run(
              (provider) => provider.rejectReceivable(receivable.id, reason),
              "crm.invoicing.vobo.done.rejected",
            );
            if (!message) close();
            return message;
          }}
        />
      ) : null}
    </Card>
  );
};

/** A payable awaiting Dirección's VoBo: approve or reject (reason). */
export const PendingPayableRow = ({ payable }: { payable: PayableSummary }) => {
  const translate = useTranslate();
  const run = useInvoicingRun();
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const approve = async () => {
    setApproving(true);
    setApproveError(null);
    const message = await run(
      (provider) => provider.approvePayable(payable.id),
      "crm.invoicing.vobo.done.approved",
    );
    setApproving(false);
    setApproveError(message);
  };

  return (
    <Card data-testid="pending-payable">
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold tabular-nums">{payable.operation_id}</h3>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="gradient"
              className="h-11 sm:h-9"
              disabled={approving}
              aria-busy={approving}
              onClick={approve}
            >
              {approving ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : null}
              {translate("crm.invoicing.vobo.approve")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={() => setRejecting(true)}
            >
              {translate("crm.invoicing.vobo.reject")}
            </Button>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Fact label={translate("crm.invoicing.fields.payee")}>
            {payable.payee_name ?? EMPTY} (
            {translate(`crm.invoicing.payee_role.${payable.payee_role}`)})
          </Fact>
          <Fact label={translate("crm.invoicing.fields.project_or_address")}>
            {payable.project_code ?? payable.property_address ?? EMPTY}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.unit")}>
            {payable.unit_identifier ?? EMPTY}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.basis")}>
            {translate(`crm.invoicing.basis.${payable.basis}`)}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.level")}>
            {payable.level_code
              ? translate(`crm.invoicing.level.${payable.level_code}`)
              : EMPTY}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.rate")}>
            {payable.rate_pct == null ? EMPTY : `${payable.rate_pct}%`}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.accumulated_sales")}>
            {payable.accumulated_sales ?? EMPTY}
          </Fact>
          <Amounts row={payable} />
        </dl>
        <LastRejection reason={payable.last_rejection_reason} />
        {approveError ? (
          <p role="alert" className="text-sm text-brand-red">
            {approveError}
          </p>
        ) : null}
      </CardContent>
      {rejecting ? (
        <ReasonDialog
          title={translate("crm.invoicing.vobo.reject_payable_title")}
          onClose={() => setRejecting(false)}
          onSubmit={async (reason) => {
            const message = await run(
              (provider) => provider.rejectPayable(payable.id, reason),
              "crm.invoicing.vobo.done.rejected",
            );
            if (!message) setRejecting(false);
            return message;
          }}
        />
      ) : null}
    </Card>
  );
};
